/**
 * A mode picked off the player waits for the player (job #343).
 *
 * A Listening or Script tap on a page like /me goes to `/` carrying the intent
 * (nav/playerIntent), and the player can only open the mode once its course
 * has loaded. That wait used to paint the Speaking screen for half a second or
 * more before the chosen mode appeared (Tom 2026-09-26: "it routes through
 * Speaking to pick up the course"). While it lasts, `pendingMode` names the
 * mode, so the container shows the neutral cover and the badge already reads
 * the chosen mode. It clears once the switch has happened, or the padlock has
 * answered, or the wait is dropped or given up.
 */
import { ref, watch } from 'vue'

export type WaitedMode = 'listening' | 'script'

export function createModeIntentWait(patienceMs = 20000) {
  const pendingMode = ref<WaitedMode | null>(null)
  let cancel: (() => void) | null = null
  // Each run is numbered, so a switch that lands after a newer run began
  // never lifts the newer run's cover.
  let generation = 0

  const finish = async (act: () => unknown, gen: number) => {
    try {
      await act()
    } catch (error) {
      // run() is fire-and-forget, so failures must be handled here.
      console.error('[modeIntentWait] Mode switch failed:', error)
    } finally {
      if (gen === generation) pendingMode.value = null
    }
  }

  /**
   * Act now if the player is ready; otherwise hold the cover and act when it
   * is. The mode stays pending until the act has finished either way, so an
   * awaited padlock check is covered too and the caller can tell "switched"
   * from "refused" by what is open once `pendingMode` clears (job #345).
   */
  function run(mode: WaitedMode | null, ready: () => boolean, act: () => unknown): void {
    drop()
    const gen = generation
    pendingMode.value = mode
    if (ready()) { void finish(act, gen); return }
    let timer: ReturnType<typeof setTimeout> | null = null
    const stop = watch(ready, (ok) => {
      if (!ok) return
      stop(); if (timer) clearTimeout(timer); cancel = null
      void finish(act, gen)
    })
    cancel = () => { stop(); if (timer) clearTimeout(timer); cancel = null; pendingMode.value = null }
    timer = setTimeout(cancel, patienceMs)
  }

  /** A mode tap while one is waiting is a change of mind: the wait is dropped. */
  function drop(): void { generation++; cancel?.(); pendingMode.value = null }

  return { pendingMode, run, drop }
}
